import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldX, ExternalLink, ArrowLeft } from 'lucide-react';

/**
 * Access Denied Page
 * Shown when a student tries to access a D2L-required lab without D2L referrer
 */
export default function AccessDenied() {
  const [searchParams] = useSearchParams();

  const reason = searchParams.get('reason');
  const labId = searchParams.get('labId');
  const studentId = searchParams.get('studentId');

  const d2lUrl = process.env.REACT_APP_D2L_URL || 'https://d2l.udst.edu.qa';

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--login-bg-start) 0%, var(--login-bg-mid) 50%, var(--login-bg-start) 100%)',
      padding: 20,
    }}>
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'var(--ad-card-bg)',
          borderRadius: 16,
          padding: 40,
          boxShadow: 'var(--login-card-shadow)',
          border: '1px solid var(--login-card-border)',
          textAlign: 'center',
        }}
        role="alert"
        aria-labelledby="access-denied-title"
      >
        {/* Icon */}
        <div style={{
          width: 72,
          height: 72,
          borderRadius: 18,
          background: 'var(--ad-danger-bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          border: '1px solid var(--ad-danger-border)',
        }} aria-hidden="true">
          <ShieldX size={36} style={{ color: 'var(--accent-danger)' }} />
        </div>

        {/* Title */}
        <h1 id="access-denied-title" style={{
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--login-heading-color)',
          marginBottom: 8,
        }}>
          Access Denied
        </h1>

        {/* Message */}
        {reason === 'd2l_required' ? (
          <>
            <p style={{
              fontSize: 16,
              color: 'var(--login-subtext-color)',
              lineHeight: 1.6,
              marginBottom: 24,
            }}>
              This lab requires you to access it through <strong style={{ color: 'var(--login-heading-color)' }}>D2L (Brightspace)</strong>.
              Please go to D2L and click on the lab link from your course.
            </p>

            <div style={{
              background: 'var(--ad-info-bg)',
              border: '1px solid var(--ad-info-border)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
            }}>
              <p style={{
                fontSize: 14,
                color: 'var(--ad-info-color)',
                marginBottom: 16,
              }}>
                <strong>Why?</strong> Your instructor has enabled D2L-only access for this lab to ensure academic integrity.
              </p>

              <a
                href={d2lUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, var(--sotp-accent-start), var(--sotp-accent-end))',
                  color: 'white',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(59, 130, 246, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <ExternalLink size={18} />
                Go to D2L
              </a>
            </div>
          </>
        ) : (
          <p style={{
            fontSize: 16,
            color: 'var(--login-subtext-color)',
            lineHeight: 1.6,
            marginBottom: 24,
          }}>
            You don't have permission to access this resource.
            Please contact your instructor if you believe this is an error.
          </p>
        )}

        {/* Student ID Info */}
        {studentId && (
          <p style={{
            fontSize: 13,
            color: 'var(--login-muted-color)',
            marginBottom: 16,
          }}>
            Student ID: {studentId}
          </p>
        )}

        {/* Back Link */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-secondary)',
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
